import instrumentData from '../data/midiInstruments.json';
import { getMidiNoteLabel } from './midiPiano';
import i18n from '../i18n';

// Two kinds of preset sit in the same list.
//
// A `sampled` one names the FluidR3 `patch` to pull and the `range`/`step` to sample it at; its
// recordings are downloaded on demand by utils/sampleLibrary and played back by useMidiPlayer.
// A `synth` one names a Tone voice class as a string plus the `options` to deep-merge into it,
// and costs nothing to load — useMidiPlayer builds it outright. A sampled download that fails
// falls back to a plain synth built in useMidiPlayer rather than to another preset, so nothing
// here needs to name a fallback.
//
// A preset of either kind may also declare `effects`: an array of Tone effect class names, in
// signal order, that useMidiPlayer builds between the voice and the master gain. That is what
// separates a supersaw from a trance lead — the chorus, the tempo-synced delay and the reverb —
// and it is why those presets are worth having at all over a sampled GM lead, which is one fixed
// recording: it cannot be detuned and it cannot be swept.
//
// `volume` is the preset's level in dB, applied on the way into useMidiPlayer's master chain —
// which ends in a limiter, so these can sit near unity without a dense chord clipping. The
// spread between them is deliberate: percussive patches (piano, guitar) peak high but decay
// away, so they need more gain to feel as loud as a sustained one (organ, pad) holding a high
// average level. The supersaws sit far lower than anything else because seven detuned oscillators
// per voice stack up fast — a four-note chord on one is 28 saws.
//
// These are still reasoned estimates, not measurements. The pass that would settle them is to
// render each voice through Tone.Offline and set its level so a five-note chord at full velocity
// peaks just under 0 dBFS — worth doing before trusting the relative balance between patches.
// Setting them per family rather than per patch is what makes 120 instruments tractable to tune
// at all: it is fifteen numbers to get roughly right instead of a hundred and twenty.
//
// The JSON behind all this is three blocks rather than one list of finished presets:
//
// - `families` — the sixteen General MIDI families (minus sound effects), each holding the
//   `range`, `step`, `volume`, `release`, `releaseTail` and `sustain` its members inherit, plus
//   the `label` the pickers group by.
// - `patches` — the 120 sampled instruments, in GM program order, as `{ name, family }` plus
//   *only* the fields that differ from the family. A patch that inherits everything is two keys.
// - `presets` — synth voices, and sampled variants that are a patch plus an effects chain
//   (Overdrive Guitar is Clean Guitar through a Tone.Distortion), written out in full.
//
// expandPatch below stitches the first two together into the same preset shape `presets` already
// uses, so everything downstream — the pickers, sampleLibrary, useMidiPlayer — sees one flat list
// and neither knows nor cares which block an instrument came from.

const { families, patches, presets, defaultId } = instrumentData;

/** A `patches` entry resolved against its family, into the preset shape the rest of the app reads. */
const expandPatch = (patch, entry) => {
  // `label` names the family, not the instrument inheriting from it — spreading it through would
  // give every preset a second name competing with its own
  const { label, ...familyDefaults } = families[entry.family];

  return {
    ...familyDefaults,
    ...entry,
    id: `sf-${patch.replace(/_/g, '-')}`,
    patch,
    kind: 'sampled',
  };
};

const expandedPatches = Object.entries(patches).map(([patch, entry]) => expandPatch(patch, entry));

// Laid out family by family, in GM program order, with each family's `presets` following its
// patches.
//
// The pickers group this list by walking it and starting a new heading whenever the family
// changes, which is only correct while a family's members are contiguous. Concatenating the two
// blocks instead — every patch, then every preset — breaks that for any preset sharing a family
// with a patch: Overdrive Guitar is a guitar, so Guitar would open once for the eight sampled
// ones and again, at the bottom of the list, for the variant.
// Every instrument, the pad's own `hidden` voices among them
export const ALL_INSTRUMENTS = Object.keys(families).flatMap((family) => [
  ...expandedPatches.filter((instrument) => instrument.family === family),
  ...presets.filter((preset) => preset.family === family),
]);

// What the pickers offer. A `hidden` preset is tuned to sit under the band, not to be picked.
export const MIDI_INSTRUMENTS = ALL_INSTRUMENTS.filter((instrument) => !instrument.hidden);

export const DEFAULT_INSTRUMENT_ID = defaultId;

/** A family's display name, for grouping a list of instruments. */
export const getFamilyLabel = (family) =>
  i18n.t(`instrumentFamilies.${family}`, { defaultValue: families[family]?.label || '' });

/** An instrument's name, kept as it is in every language; only its family is translated. */
export const getInstrumentName = (instrument) => instrument.name;

/**
 * The families to offer as categories, in the order they appear in MIDI_INSTRUMENTS.
 *
 * Read back off the list rather than straight off the `families` block so the two cannot drift:
 * a family that ends up holding nothing never becomes a category leading to an empty screen, and
 * the categories always run in the same order as the list they filter.
 */
export const INSTRUMENT_FAMILIES = MIDI_INSTRUMENTS.reduce((found, { family }) => {
  if (!found.some((entry) => entry.family === family)) found.push({ family, label: getFamilyLabel(family) });

  return found;
}, []);

// Ids that parts still carry from earlier versions of this list, each mapped to what it is now.
//
// The first group is the original synth-only presets, from before anything was sampled. The
// second is the curated sampled ids, which were hand-named (`sf-piano`) before the set was opened
// up to all of General MIDI and ids became mechanical (`sf-acoustic-grand-piano`). Both keep an
// existing song sounding like what it was set to rather than silently collapsing onto the default.
//
// Bells finally has a real counterpart: it used to fall back to the piano for want of a struck
// voice in the set, and `tubular_bells` is the patch it was imitating all along.
const LEGACY_INSTRUMENT_IDS = {
  piano: 'sf-acoustic-grand-piano',
  'electric-piano': 'sf-electric-piano-1',
  organ: 'sf-church-organ',
  strings: 'sf-string-ensemble-1',
  brass: 'sf-brass-section',
  bass: 'sf-electric-bass-finger',
  bells: 'sf-tubular-bells',

  'sf-piano': 'sf-acoustic-grand-piano',
  'sf-electric-piano': 'sf-electric-piano-1',
  'sf-organ': 'sf-church-organ',
  'sf-strings': 'sf-string-ensemble-1',
  'sf-brass': 'sf-brass-section',
  'sf-pad': 'sf-pad-2-warm',
  'sf-choir': 'sf-choir-aahs',
  'sf-nylon-guitar': 'sf-acoustic-guitar-nylon',
  'sf-bass': 'sf-electric-bass-finger',
};

// Every note in the FluidR3 set is a 128kbps MP3 of the same fixed length, so file count is a
// good enough stand-in for download size to put in front of the user
const SAMPLE_KB_EACH = 25;

/**
 * Look up a preset, falling back to the default when the id is missing or no longer exists
 * (a part saved with an instrument that was later removed still has to play).
 */
export function getInstrument(instrumentId) {
  const id = LEGACY_INSTRUMENT_IDS[instrumentId] || instrumentId;

  return (
    ALL_INSTRUMENTS.find((instrument) => instrument.id === id) ||
    MIDI_INSTRUMENTS.find((instrument) => instrument.id === DEFAULT_INSTRUMENT_ID) ||
    MIDI_INSTRUMENTS[0]
  );
}

/**
 * The instrument a part should play with by default.
 *
 * Resolved through getInstrument so a part carrying a retired synth id reports the sampled
 * instrument it now maps to — otherwise the picker would sit on a value not in its own list and
 * render blank.
 */
export function getPartInstrumentId(midiJson) {
  return getInstrument(midiJson?.instrumentId || DEFAULT_INSTRUMENT_ID).id;
}

/**
 * The instrument a part's bar-line chord chart should be auditioned with.
 *
 * Its own property rather than the one above: that id belongs to an uploaded MIDI clip, and a part
 * can hold a chart with no clip at all. Resolved through getInstrument for the same reason.
 *
 * The part's own instrument wins, then the song's default, then the app's.
 */
export function getChartInstrumentId(part, song) {
  return getInstrument(getChartInstrumentOverride(part) || song?.chartInstrumentId || DEFAULT_INSTRUMENT_ID).id;
}

/**
 * The instrument a part sets for its chart over its song's default, or null where it follows it.
 *
 * Charts saved before songs had a default always stored one, the app's own unless someone changed
 * it — so the app's default reads as no choice, and a part can't override back to it.
 */
export function getChartInstrumentOverride(part) {
  const id = part?.chartInstrumentId && getInstrument(part.chartInstrumentId).id;

  return id && id !== DEFAULT_INSTRUMENT_ID ? id : null;
}

/** Whether a preset plays from downloaded recordings rather than a generated voice. */
export const isSampledInstrument = (preset) => preset?.kind === 'sampled';

/**
 * Whether a preset's recordings are of a note that holds for as long as it is played.
 *
 * Every FluidR3 file is the same fixed-length render, so a held note runs out of recording after
 * about three seconds no matter what it is. On a piano or a plucked guitar that is inaudible —
 * the note has decayed away by then — but an organ, a bowed string or a pad is still at full
 * level when the file ends, and simply stops. Saying so here is what has sampleLibrary loop the
 * sustaining part of the recording rather than let it run out; see LOOP_START_SECONDS there.
 *
 * It is a property of the sound and not of the family: `strings` covers both a bowed cello and a
 * pizzicato one, so a few patches answer differently from the family they inherit from.
 */
export const isSustainedInstrument = (preset) => isSampledInstrument(preset) && preset.sustain === true;

/**
 * The notes to actually download for a sampled preset.
 *
 * Sampling every semitone would be ~85 files; stepping through the range in minor thirds cuts
 * that to a third of the size and leaves Tone.Sampler repitching by at most one semitone, which
 * is inaudible on these sources. Names come out with flats (Db, Eb, Gb, Ab, Bb) because that is
 * exactly how the FluidR3 files are named.
 *
 * @param {object} preset - A sampled instrument preset
 * @returns {Array<{ note: string, midi: number }>} empty for a synth preset
 */
export function getSampleNotes(preset) {
  if (!isSampledInstrument(preset)) return [];

  const { low, high } = preset.range;
  const step = preset.step || 3;
  const refused = REFUSED_SAMPLES[preset.patch] || [];
  const notes = [];

  for (let midi = low; midi <= high; midi += step) {
    const note = getMidiNoteLabel(midi, false);

    if (!refused.includes(note)) notes.push({ note, midi });
  }

  return notes;
}

// Files jsDelivr answers 403 for, by patch. Left out, the sampler repitches from the notes either
// side; requested, one failure sends the whole instrument to its synth fallback.
const REFUSED_SAMPLES = {
  string_ensemble_2: ['Gb3'],
  electric_guitar_muted: ['E2', 'G4'],
  overdriven_guitar: ['Db4', 'E5'],
  violin: ['G4', 'Db5'],
};

/** Rough download size of a sampled preset, for labelling the choice before it is made. */
export const getInstrumentSizeKb = (preset) => getSampleNotes(preset).length * SAMPLE_KB_EACH;
